import { LitElement, html, css } from 'lit';
import { cancelOffer } from '../../../api/head-apis';

class CancelOfferDialog extends LitElement {
  static get properties() {
    return {
      reasons: { type: Array },
      loading: { type: Boolean },
      error: { type: String },
      onConfirm: { type: Function },
      onCancel: { type: Function },
      conversationId: { type: String },
      token: { type: String },
      offerId: { type: String },
      exchangeAssetId: { type: String },
    };
  }

  constructor() {
    super();
    this.reasons = [];
    this.loading = false;
    this.error = '';
    this.selectedReasonId = null;
    this.otherReasonText = '';
    this.isOtherSelected = false;
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('offerId')) {
      console.log('Dialog received offerId:', this.offerId);
    }
    if (changedProperties.has('onConfirm')) {
      console.log('Dialog received onConfirm:', typeof this.onConfirm);
    }
    if (changedProperties.has('onCancel')) {
      console.log('Dialog received onCancel:', typeof this.onCancel);
    }
  }

  static styles = css`
    :host {
      --popup-bg: var(--bg-primary-color);
      --popup-text: var(--text-primary-color);
      --popup-option-bg: var(--bg-secondary-color);
      --popup-option-bg-selected: var(--base-color);
      --popup-option-text: var(--text-primary-color);
      --popup-option-text-selected: var(--white);
      --popup-btn-bg: var(--base-color);
      --popup-btn-text: var(--white);
      --popup-btn-radius: 8px;
      --popup-btn-font: 16px;
      --popup-overlay-bg: var(--popup-overlay);
      --popup-error: var(--text-danger-color);
      --popup-border: var(--border-color);
      --popup-subtitle: var(--text-secondary-color);
      --popup-hover-bg: var(--bg-grey-color);
      display: block;
      min-width: 340px;
      max-width: 95vw;
    }
    .dialog-content {
      background: var(--popup-bg);
      color: var(--popup-text);
      border-radius: 18px;
      padding: 32px 28px 24px 28px;
      box-shadow: var(--menu-shadow);
      position: relative;
      display: flex;
      flex-direction: column;
      max-width: 95vw;
    }
    .dialog-header {
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      margin-bottom: 12px;
    }
    .dialog-header span {
      font-size: 22px;
      font-weight: 700;
      flex: 1;
      text-align: center;
      color: var(--popup-text);
    }
    .close-btn {
      position: absolute;
      right: 0;
      top: 0;
      background: none;
      border: none;
      color: var(--popup-text);
      font-size: 28px;
      cursor: pointer;
      padding: 0 4px;
      line-height: 1;
      border-radius: 50%;
      transition: background 0.15s;
    }
    .close-btn:hover {
      background: var(--popup-hover-bg);
    }
    .dialog-body {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }
    .dialog-body label.subtitle {
      font-size: 15px;
      color: var(--popup-subtitle);
      margin-bottom: 8px;
      font-weight: 500;
      text-align: left;
      display: block;
    }
    .dialog-options {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-bottom: 8px;
    }
    .dialog-option {
      background: var(--popup-option-bg);
      color: var(--popup-option-text);
      border-radius: 10px;
      padding: 14px 16px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      border: 2px solid transparent;
      outline: none;
      transition: background 0.15s, border 0.15s, color 0.15s;
      user-select: none;
      text-align: left;
      display: flex;
      align-items: center;
      margin-bottom: 0;
    }
    .dialog-option.selected {
      background: var(--popup-option-bg-selected);
      color: var(--popup-option-text-selected);
      border: 2px solid var(--popup-option-bg-selected);
    }
    .dialog-option:focus {
      border: 2px solid var(--popup-btn-bg);
    }
    .dialog-option:last-child {
      margin-bottom: 0;
    }
    .dialog-other-textarea {
      margin-top: 8px;
      border-radius: 8px;
      border: 1px solid var(--popup-border);
      background: var(--popup-option-bg);
      color: var(--popup-option-text);
      font-size: 15px;
      padding: 10px;
      resize: vertical;
      min-height: 60px;
      outline: none;
      transition: border 0.15s;
    }
    .dialog-other-textarea:focus {
      border: 1.5px solid var(--popup-btn-bg);
    }
    .dialog-body-buttons {
      display: flex;
      flex-direction: column;
      gap: 0;
      margin-top: 18px;
    }
    .dialog-apply-btn {
      background: var(--popup-btn-bg);
      color: var(--popup-btn-text);
      border: none;
      border-radius: var(--popup-btn-radius);
      font-size: var(--popup-btn-font);
      font-weight: 600;
      padding: 14px 0;
      width: 100%;
      margin-top: 8px;
      cursor: pointer;
      transition: background 0.15s, color 0.15s;
      box-shadow: 0 2px 8px rgba(109,62,193,0.08);
    }
    .dialog-apply-btn:disabled {
      background: var(--text-info-color);
      color: var(--popup-hover-bg);
      cursor: not-allowed;
    }
    .dialog-error {
      color: var(--popup-error);
      margin-top: 8px;
      font-size: 14px;
      text-align: left;
    }
  `;

  handleReasonChange(e) {
    const value = e.target.value;
    this.selectedReasonId = value;
    this.isOtherSelected = value === 'other';
    if (!this.isOtherSelected) {
      this.otherReasonText = '';
    }
    this.error = '';
    this.requestUpdate();
  }

  handleOtherInput(e) {
    this.otherReasonText = e.target.value;
    this.requestUpdate();
  }

  async handleApply() {
    console.log('handleApply called');
    console.log('onConfirm function:', this.onConfirm);
    console.log('selectedReasonId:', this.selectedReasonId);
    console.log('isOtherSelected:', this.isOtherSelected);
    console.log('otherReasonText:', this.otherReasonText);
    console.log('reasons:', this.reasons);
    
    let reasonToSend = '';
    if (this.isOtherSelected) {
      reasonToSend = this.otherReasonText.trim();
      if (!reasonToSend) {
        this.error = 'Please enter a reason.';
        this.requestUpdate();
        return;
      }
    } else {
      // Find the selected reason object to get the reason text
      const selectedReason = this.reasons?.find(reason => String(reason._id) === this.selectedReasonId);
      if (!selectedReason) {
        this.error = 'Please select a reason.';
        this.requestUpdate();
        return;
      }
      reasonToSend = selectedReason.reason; // Use the reason text, not the ID
    }
    
    if (!reasonToSend || reasonToSend === 'null') {
      this.error = 'Please select a reason.';
      this.requestUpdate();
      return;
    }
  
    console.log('Final reasonToSend:', reasonToSend);
    
    // Close dialog immediately and let parent handle the API call
    if (this.onConfirm) {
      console.log('Calling onConfirm with reason:', reasonToSend);
      this.onConfirm(reasonToSend);
    } else {
      console.error('onConfirm function is not available!');
    }
  }

  render() {
    return html`
      <div class="dialog-content">
        <div class="dialog-header">
          <span>Cancel Offer</span>
          <button class="close-btn" @click=${() => this.onCancel && this.onCancel()} aria-label="Close popup">&times;</button>
        </div>
        <div class="dialog-body">
          <label class="subtitle">Please select an option</label>
          <div class="dialog-options" role="radiogroup">
            ${this.loading
              ? html`<div>Fetching Reasons...</div>`
              : html`
                  ${this.reasons?.map(
                    reason => html`
                      <label class="dialog-option ${this.selectedReasonId === String(reason._id) ? 'selected' : ''}" style="display:flex;align-items:center;gap:8px;" @click=${() => this.handleReasonChange({target: {value: String(reason._id)}})}>
                        <input
                          type="radio"
                          name="cancel-reason"
                          .value=${String(reason._id)}
                          .checked=${this.selectedReasonId === String(reason._id)}
                          @change=${this.handleReasonChange}
                          @click=${this.handleReasonChange}
                          aria-checked="${this.selectedReasonId === String(reason._id)}"
                        />
                        <span>${reason.reason}</span>
                      </label>
                    `
                  )}
                  <label class="dialog-option ${this.selectedReasonId === 'other' ? 'selected' : ''}" style="display:flex;align-items:center;gap:8px;" @click=${() => this.handleReasonChange({target: {value: 'other'}})}>
                    <input
                      type="radio"
                      name="cancel-reason"
                      value="other"
                      .checked=${this.selectedReasonId === 'other'}
                      @change=${this.handleReasonChange}
                      @click=${this.handleReasonChange}
                      aria-checked="${this.selectedReasonId === 'other'}"
                    />
                    <span>Other</span>
                  </label>
                `}
          </div>
          ${this.isOtherSelected
            ? html`
                <textarea
                  class="dialog-other-textarea"
                  rows="3"
                  placeholder="Enter your reason..."
                  .value=${this.otherReasonText}
                  @input=${this.handleOtherInput}
                  aria-label="Other reason"
                ></textarea>
              `
            : ''}
          ${this.error
            ? html`<div class="dialog-error">${this.error}</div>`
            : ''}
          <div class="dialog-body-buttons">
            <button class="dialog-apply-btn" @click=${this.handleApply}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('cancel-offer-dialog', CancelOfferDialog); 